/* Reference point management.
 *
 * Only shown while the `refpoint` tool is active — reference points are authoring
 * furniture, not something an operator needs in view while flying a mission.
 */
import { useState } from 'react'
import { Check, MapPin, Pencil, Trash2, X } from 'lucide-react'
import { useDeck } from '../state/store'
import { formatLatLon } from '../lib/format'
import { ZONE_LABEL } from '../data/refpoints'

export function RefPointsPanel() {
  const tool = useDeck((s) => s.tool)
  const refPoints = useDeck((s) => s.refPoints)
  const zones = useDeck((s) => s.zones)
  const rename = useDeck((s) => s.refPointRename)
  const remove = useDeck((s) => s.refPointRemove)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  if (tool !== 'refpoint') return null

  const commit = (id: string) => {
    const clean = draft.trim().toUpperCase()
    if (clean) rename(id, clean)
    setEditing(null)
  }

  return (
    <div className="panel absolute left-3 top-3 z-20 w-72 overflow-hidden">
      <div
        className="flex items-center gap-1.5 px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <MapPin size={12} aria-hidden />
        <span className="title">Reference points</span>
        <span className="num ml-auto" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
          {refPoints.length}
        </span>
      </div>

      <p className="px-3 py-1.5" style={{ fontSize: 10.5, color: 'var(--color-text-3)' }}>
        Click the map to add. Drag a point to move it — anything bound to it follows.
      </p>

      <div className="max-h-56 overflow-y-auto">
        {refPoints.length === 0 && (
          <p className="px-3 py-3" style={{ fontSize: 10.5, color: 'var(--color-text-3)' }}>
            No reference points in this exercise. They travel in the scenario document, and the
            <span className="num"> R </span> tool adds them locally for this session.
          </p>
        )}
        {refPoints.map((r) => (
          <div
            key={r.id}
            className="flex items-center gap-2 px-3 py-1"
            style={{ borderBottom: '1px solid var(--color-line-1)' }}
          >
            {editing === r.id ? (
              <>
                <input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commit(r.id)
                    if (e.key === 'Escape') setEditing(null)
                  }}
                  className="num min-w-0 flex-1 rounded px-1 outline-none"
                  style={{
                    background: 'var(--color-surface-0)',
                    border: '1px solid var(--color-accent)',
                    fontSize: 11,
                    color: 'var(--color-text-1)',
                  }}
                  aria-label={`Rename ${r.name}`}
                />
                <button
                  className="ctl"
                  style={{ width: 18, height: 18, padding: 0 }}
                  onClick={() => commit(r.id)}
                  aria-label="Confirm rename"
                >
                  <Check size={10} />
                </button>
              </>
            ) : (
              <>
                <span className="num shrink-0" style={{ fontSize: 11, minWidth: 76 }}>
                  {r.name}
                </span>
                <span
                  className="num min-w-0 flex-1 truncate"
                  style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}
                >
                  {formatLatLon(r.lat, r.lon)}
                </span>
                {r.area && (
                  <span className="label shrink-0" style={{ fontSize: 8 }} title="Bound to a mission area">
                    {r.area}
                  </span>
                )}
                <button
                  className="ctl shrink-0"
                  style={{ width: 18, height: 18, padding: 0 }}
                  onClick={() => {
                    setEditing(r.id)
                    setDraft(r.name)
                  }}
                  aria-label={`Rename ${r.name}`}
                >
                  <Pencil size={10} />
                </button>
                <button
                  className="ctl shrink-0"
                  style={{ width: 18, height: 18, padding: 0 }}
                  onClick={() => remove(r.id)}
                  disabled={!!r.area}
                  aria-label={`Delete ${r.name}`}
                  title={
                    r.area
                      ? `Bound to ${r.area} — detach it from the mission first`
                      : `Delete ${r.name}`
                  }
                >
                  <Trash2 size={10} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Zones are read-only for now — drawing them needs a polygon tool. */}
      <div className="label px-3 pb-1 pt-2">Exclusion zones</div>
      {zones.map((z) => (
        <div
          key={z.id}
          className="flex items-center gap-2 px-3 py-1"
          style={{ borderBottom: '1px solid var(--color-line-1)' }}
        >
          <X size={10} style={{ color: 'var(--color-text-3)' }} aria-hidden />
          <span className="num min-w-0 flex-1 truncate" style={{ fontSize: 10.5 }}>
            {z.name}
          </span>
          <span className="label shrink-0" style={{ fontSize: 8 }}>
            {ZONE_LABEL[z.kind]}
          </span>
        </div>
      ))}
      <p className="px-3 py-1.5" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
        Zone drawing needs a polygon tool — not in v0.9.
      </p>
    </div>
  )
}
